import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FieldValues,
  FieldErrors,
  UseControllerProps,
  useFormContext,
  useFieldArray,
} from 'react-hook-form';
import FormField from '@ingka/form-field';
import Combobox, { MenuItem } from '@ingka/combobox';
import Pill from '@ingka/pill';
import crossIcon from '@ingka/ssr-icon/paths/cross';

import { getter } from '@/lib/utils/object';
import { IFormContext } from '@/components/page-widgets/create-code/code-settings';
import { extractErrorMessage } from '../error-message';

export type DropdownItem = {
  value: string;
  i18nKey: string;
};

type AllowedFormKey = 'store.department' | 'store.component';

export type ControlledComboboxProps<T extends FieldValues> = {
  className?: string;
  // Form
  name: AllowedFormKey;
  errors: FieldErrors<T>;
  rules?: UseControllerProps<T>['rules'];
  forcedError?: any;
  caption?: string;
  // Ingka form field props
  readOnly?: boolean;
  items: DropdownItem[];
  label: string;
  shouldValidate?: boolean;
  successMessage?: string;
  placeholder?: string;
};

export function ControlledCombobox<T extends FieldValues>(
  props: PropsWithChildren<ControlledComboboxProps<T>>
) {
  const { t } = useTranslation();
  const {
    name,
    errors,
    shouldValidate = true,
    label,
    successMessage,
    forcedError,
    items,
    readOnly,
    caption,
    placeholder,
  } = props;
  // Error handling
  const error = forcedError || getter(errors, name);
  const errorMsgArgs = error && extractErrorMessage(error);
  const errorMsg = error && t(...errorMsgArgs);

  // Hooks
  const { control }: IFormContext = useFormContext();
  const { remove, append, replace } = useFieldArray({
    control,
    name,
  });
  const { watch } = useFormContext();

  // Event handlers
  const toggleSelection = (value: string) => {
    if (isSelected(value)) {
      const index = findIndex(value);
      remove(index);
    } else {
      append({ value });
    }
  };

  const removeItem = (item: string) => {
    const index = findIndex(item);
    remove(index);
  };

  const clearItems = () => {
    replace([]);
  };

  // Data
  const selectedItems = watch(name);

  const findIndex = (value: string): number => {
    if (selectedItems) {
      for (let i = 0; i < selectedItems.length; i++) {
        if (selectedItems[i].value == value) return i;
      }
    }
    return -1;
  };

  const isSelected = (value: string): boolean => {
    return findIndex(value) >= 0;
  };

  const selectedComboItems = selectedItems
    ? selectedItems.map((item: any) => item.value)
    : [];

  const selectedPillItems = selectedItems
    ? selectedItems
        .map((item: any) => {
          return items.find((i) => i.value === item.value);
        })
        .filter((i: any) => !!i)
    : [];

  return (
    <div className={props.className}>
      <FormField
        fieldHelper={{
          msg: shouldValidate ? successMessage : '',
          type: 'error',
        }}
        validation={{
          msg: errorMsg,
          type: 'error',
        }}
        valid={!error}
        shouldValidate={!!shouldValidate}
      >
        <Combobox
          id={name}
          label={label}
          labelId="label-id"
          listboxId="combobox-listbox"
          disabled={readOnly}
          multiple={true}
          value={selectedComboItems}
          placeholder={placeholder}
          onClearClicked={clearItems}
        >
          {items.map(({ value, i18nKey }) => (
            <MenuItem
              key={value}
              title={t(i18nKey)}
              id={value}
              onClick={() => toggleSelection(value)}
            />
          ))}
        </Combobox>
        {caption && <small className="text-slate-600">{caption}</small>}
      </FormField>
      {selectedPillItems && (
        <div className="my-3 flex flex-wrap gap-[10px]">
          {selectedPillItems.map((item: any) => (
            <Pill
              key={t(item.value)}
              iconPosition="trailing"
              label={t(item.i18nKey)}
              size="small"
              ssrIcon={crossIcon}
              onClick={() => {
                removeItem(item.value);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ControlledCombobox;
